import {
  getLeoUuid,
  LeoUUID,
  getObject,
  getList,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCError,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
import { Code, CodeDTO } from "../types/code";
import { RespondentColumnName, RespondentColumnNameDTO } from "../types/respondentColumnName";
/* eslint-disable import/export */
export abstract class GetSurveyRuleKeyOptionsRPC implements
  LeoRPC<GetSurveyRuleKeyOptionsRPC.Request, GetSurveyRuleKeyOptionsRPC.Response, GetSurveyRuleKeyOptionsRPC.Errors.Errors>
{
  abstract execute(
    request: GetSurveyRuleKeyOptionsRPC.Request
  ): Promise<LeoRPCResult<GetSurveyRuleKeyOptionsRPC.Response, GetSurveyRuleKeyOptionsRPC.Errors.Errors>>;
}

export namespace GetSurveyRuleKeyOptionsRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  
  export namespace RequestEnums {
    /* eslint-disable @typescript-eslint/no-shadow */
    export namespace KeyType {
      export enum KeyType {
        QUESTION = "QUESTION",
        RESPONDENT = "RESPONDENT",
      }
    
      export interface KeyTypeDTO {
        case: string;
      }
    
      export const fromDTO = (dto: KeyTypeDTO): KeyType => {
        if (dto === undefined || dto === null) {
          throw new InvalidRequestKeyTypeError(
            `Invalid DTO ${dto} provided for KeyType.KeyType`
          );
        }
        switch (dto.case) {
          case "QUESTION":
            return KeyType.QUESTION;
          case "RESPONDENT":
            return KeyType.RESPONDENT;
          default:
            throw new InvalidRequestKeyTypeError(
              `Case ${dto.case} is not valid case of KeyType.KeyType`
            );
        }
      };
    
      export const toDTO = (keyType: KeyType): KeyTypeDTO => {
        const enumDTO = {
          case: KeyType[keyType],
        };
        return enumDTO;
      };
    }
    
  }
  export interface RequestDTO {
    surveyId: string;
    keyType: RequestEnums.KeyType.KeyTypeDTO;
  }
  
  export class Request implements LeoRPCRequest {
    readonly isGetSurveyRuleKeyOptionsRPCRequest: boolean = true;
  
    readonly surveyId: LeoUUID;
    readonly keyType: RequestEnums.KeyType.KeyType;
  
  
    constructor(
    surveyId: LeoUUID, 
    keyType: RequestEnums.KeyType.KeyType
    ) {
      this.surveyId = surveyId;
      this.keyType = keyType;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const surveyId = getLeoUuid(dto, "surveyId", InvalidRequestSurveyIdError);
      const keyType = RequestEnums.KeyType.fromDTO(getObject(dto, "keyType", InvalidRequestKeyTypeError));
      return new Request(
        surveyId,
        keyType
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          surveyId: this.surveyId.toDTO(),
          keyType: RequestEnums.KeyType.toDTO(this.keyType)
      };
      return dto;
    }
    }
  
  Request.prototype.toString = function toString(): string {
    return `GetSurveyRuleKeyOptionsRPC(surveyId='${this.surveyId}',keyType='${this.keyType}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  
  export namespace ResponseEnums {
    export namespace KeyOption {
      export interface QuestionCodeDTO {
        case: string;
        codes: CodeDTO[];
      }
      
      export class QuestionCode {
        readonly isKeyOptionQuestionCode: boolean = true;
      
        readonly codes: Code[];
      
      
        constructor(
        codes: Code[]
        ) {
          this.codes = codes;
        }
      
        static fromDTO(dto: QuestionCodeDTO): QuestionCode {
          const codes = getList<CodeDTO, Code>(
            dto,
            "codes",
            Code.fromDTO,
            InvalidResponseKeyOptionQuestionCodeCodesError
          );
          return new QuestionCode(
            codes
          );
        }
      
        toDTO(): QuestionCodeDTO {
          const dto: QuestionCodeDTO = {
            case: "QUESTION_CODE",
              codes: this.codes.map((e: Code) => { return e.toDTO() })
          };
          return dto;
        }
        }
      
      QuestionCode.prototype.toString = function toString(): string {
        return `KeyOption.QUESTION_CODE(codes='${this.codes}')`;
      };
      
      export interface RespondentColumnNamesDTO {
        case: string;
        names: RespondentColumnNameDTO[];
      }
      
      export class RespondentColumnNames {
        readonly isKeyOptionRespondentColumnNames: boolean = true;
      
        readonly names: RespondentColumnName[];
      
      
        constructor(
        names: RespondentColumnName[]
        ) {
          this.names = names;
        }
      
        static fromDTO(dto: RespondentColumnNamesDTO): RespondentColumnNames {
          const names = getList<RespondentColumnNameDTO, RespondentColumnName>(
            dto,
            "names",
            RespondentColumnName.fromDTO,
            InvalidResponseKeyOptionRespondentColumnNamesNamesError
          );
          return new RespondentColumnNames(
            names
          );
        }
      
        toDTO(): RespondentColumnNamesDTO {
          const dto: RespondentColumnNamesDTO = {
            case: "RESPONDENT_COLUMN_NAMES",
              names: this.names.map((e: RespondentColumnName) => { return e.toDTO() })
          };
          return dto;
        }
        }
      
      RespondentColumnNames.prototype.toString = function toString(): string {
        return `KeyOption.RESPONDENT_COLUMN_NAMES(names='${this.names}')`;
      };
      
    
      export type KeyOption = QuestionCode | RespondentColumnNames;
    
      export type KeyOptionDTO = QuestionCodeDTO | RespondentColumnNamesDTO;
    
      export const fromDTO = (dto: KeyOptionDTO): KeyOption => {
        switch (dto.case) {
          case "QUESTION_CODE":
              return QuestionCode.fromDTO(dto as QuestionCodeDTO);
          case "RESPONDENT_COLUMN_NAMES":
              return RespondentColumnNames.fromDTO(dto as RespondentColumnNamesDTO);
          default:
            throw new InvalidResponseKeyOptionError(
                `Case ${dto.case} is not valid case of KeyOption.`
            );
        }
      };
    }
    
  }
  export interface ResponseDTO {
    keyOption: ResponseEnums.KeyOption.KeyOptionDTO;
  }
  
  export class Response implements LeoRPCResponse {
    readonly isGetSurveyRuleKeyOptionsResponse: boolean = true;
  
    readonly keyOption: ResponseEnums.KeyOption.KeyOption;
  
  
    constructor(
    keyOption: ResponseEnums.KeyOption.KeyOption
    ) {
      this.keyOption = keyOption;
    }
  
    static fromDTO(dto: ResponseDTO): Response {
      const keyOption = ResponseEnums.KeyOption.fromDTO(getObject(dto, "keyOption", InvalidResponseKeyOptionError));
      return new Response(
        keyOption
      );
    }
  
    toDTO(): ResponseDTO {
      const dto: ResponseDTO = {
          keyOption: this.keyOption.toDTO()
      };
      return dto;
    }
    }
  
  Response.prototype.toString = function toString(): string {
    return `GetSurveyRuleKeyOptions(keyOption='${this.keyOption}')`;
  };
  

  export enum RPCError {
    InvalidSurveyId = "INVALID_SURVEY_ID"
  }

  export namespace Errors {
    
    export interface InvalidSurveyIdDTO {
      code: string;
    }
    
    export class InvalidSurveyId extends LeoRPCError<RPCError> {
      readonly isInvalidSurveyId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidSurveyId;
        super(code);
      }
    
    
      toDTO(): InvalidSurveyIdDTO {
        const dto: InvalidSurveyIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidSurveyId.prototype.toString = function toString(): string {
      return `GetSurveyRuleKeyOptionsRPC.INVALID_SURVEY_ID()`;
    };
    

    export type Errors = InvalidSurveyId;
    export type ErrorsDTO = InvalidSurveyIdDTO;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        case RPCError.InvalidSurveyId:
          return new InvalidSurveyId();
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestSurveyIdError extends GetSurveyRuleKeyOptionsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestSurveyIdError";
  }
}

export class InvalidRequestKeyTypeError extends GetSurveyRuleKeyOptionsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestKeyTypeError";
  }
}

export class InvalidRequestKeyTypeQuestionError extends InvalidRequestKeyTypeError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestKeyTypeQuestionError";
  }
}

export class InvalidRequestKeyTypeRespondentError extends InvalidRequestKeyTypeError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestKeyTypeRespondentError";
  }
}

export class InvalidResponseKeyOptionError extends GetSurveyRuleKeyOptionsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseKeyOptionError";
  }
}

export class InvalidResponseKeyOptionQuestionCodeError extends InvalidResponseKeyOptionError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseKeyOptionQuestionCodeError";
  }
}

export class InvalidResponseKeyOptionQuestionCodeCodesError extends InvalidResponseKeyOptionQuestionCodeError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseKeyOptionQuestionCodeCodesError";
  }
}

export class InvalidResponseKeyOptionRespondentColumnNamesError extends InvalidResponseKeyOptionError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseKeyOptionRespondentColumnNamesError";
  }
}

export class InvalidResponseKeyOptionRespondentColumnNamesNamesError extends InvalidResponseKeyOptionRespondentColumnNamesError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseKeyOptionRespondentColumnNamesNamesError";
  }
}

export class InvalidResponseInvalidSurveyIdError extends GetSurveyRuleKeyOptionsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidSurveyIdError";
  }
}

